import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Download, XCircle, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const BADGE = { confirmed: 'bg-green-100 text-green-700', waitlisted: 'bg-yellow-100 text-yellow-700', cancelled: 'bg-gray-200 text-gray-600' };
const ORG = { company: 'Company', college: 'College / University', startup: 'Startup', other: 'Other' };

export const ConvergeRsvpAdmin = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [cap, setCap] = useState('');
  const [filter, setFilter] = useState('all');

  const load = async () => {
    try {
      const r = (await axios.get(`${API}/api/admin/super/converge/rsvps`, { headers })).data;
      setData(r);
      setCap(r.cap ?? '');
    } catch { toast.error('Failed to load RSVPs'); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveCap = async () => {
    try {
      const r = await axios.put(`${API}/api/admin/super/converge/rsvp-settings`, { cap: cap === '' ? null : Number(cap) }, { headers });
      toast.success(`Seat limit saved${r.data.promoted ? ` · ${r.data.promoted} moved off the waitlist` : ''}`);
      load();
    } catch (e) { toast.error('Enter a whole number ≥ 1, or leave empty for no limit'); }
  };
  const act = async (r, action) => {
    if (action === 'cancel' && !window.confirm(`Cancel ${r.name}'s RSVP?`)) return;
    try {
      const res = await axios.post(`${API}/api/admin/super/converge/rsvps/${r.id}/${action}`, {}, { headers });
      toast.success(res.data.message + (res.data.promoted ? ` · ${res.data.promoted} promoted from waitlist` : ''));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };
  const exportCsv = async () => {
    let res;
    try { res = await axios.get(`${API}/api/admin/super/converge/rsvps.csv`, { headers, responseType: 'blob' }); } catch { toast.error('Export failed'); return; }
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'converge-hr-conclave-rsvps.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-400" /></div>;
  const rows = data.rsvps.filter((r) => filter === 'all' || r.status === filter);
  return (
    <div className="space-y-4 text-gray-900" data-testid="converge-rsvp-admin">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[['Confirmed', data.counts.confirmed, 'text-green-700'], ['Waitlisted', data.counts.waitlisted, 'text-yellow-700'], ['Cancelled', data.counts.cancelled, 'text-gray-600'], ['Seat limit', data.cap ?? 'None', 'text-gray-900']].map(([l, v, c]) => (
          <div key={l} className="p-3 bg-white border rounded" data-testid={`rsvp-stat-${l.toLowerCase().replace(' ', '-')}`}><p className="text-xs text-gray-500 uppercase">{l}</p><p className={`text-2xl font-bold ${c}`}>{v}</p></div>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-xs font-semibold text-gray-600 mb-1">Seat limit (empty = no limit)</span>
          <input type="number" min="1" value={cap} onChange={(e) => setCap(e.target.value)} className="w-40 px-3 py-2 border border-gray-300 rounded text-sm" data-testid="rsvp-cap-input" />
        </label>
        <button onClick={saveCap} className="px-3 py-2 bg-blue-600 text-white rounded text-sm font-semibold" data-testid="rsvp-cap-save-btn">Save limit</button>
        <div className="flex gap-1 ml-auto">
          {['all', 'confirmed', 'waitlisted', 'cancelled'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded text-sm ${filter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid={`rsvp-filter-${f}`}>{f[0].toUpperCase() + f.slice(1)}</button>
          ))}
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 rounded text-sm font-semibold" data-testid="rsvp-export-btn"><Download size={14} /> CSV</button>
      </div>
      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b"><tr>{['Name', 'Organisation', 'Contact', 'City', 'Status', ''].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold text-gray-600">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`rsvp-row-${r.id}`}>
                <td className="px-3 py-2"><p className="font-medium text-gray-900">{r.name}</p><p className="text-xs text-gray-500">{r.designation}</p></td>
                <td className="px-3 py-2"><p>{r.organisation}</p><p className="text-xs text-gray-500">{ORG[r.org_type]}</p></td>
                <td className="px-3 py-2 text-xs"><p>{r.email}</p><p className="text-gray-500">{r.phone}</p></td>
                <td className="px-3 py-2 text-xs">{r.city}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs font-medium ${BADGE[r.status]}`}>{r.status}</span></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.status === 'waitlisted' && <button onClick={() => act(r, 'confirm')} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Confirm from waitlist" data-testid={`rsvp-confirm-${r.id}`}><CheckCircle size={15} /></button>}
                  {r.status !== 'cancelled' && <button onClick={() => act(r, 'cancel')} className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Cancel RSVP" data-testid={`rsvp-cancel-${r.id}`}><XCircle size={15} /></button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No RSVPs yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
